/**
 * Tipos de orden — qué órdenes maneja la empresa y qué pide cada una.
 *
 * El ERP no sabe de carros ni de cortes de pelo: cada tipo trae sus propios
 * campos (placa, serial, fórmula de color…) y sus pasos (recibido →
 * diagnóstico → listo…). Se arranca de una plantilla de la biblioteca de
 * NARA y se ajusta aquí; la plantilla se copia, así que cambiarla no toca lo
 * que ya tiene la empresa.
 *
 * Sin ningún tipo activo la empresa no usa órdenes y el menú no las muestra.
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, Card, CardActionArea, Chip, IconButton, Dialog,
  DialogTitle, DialogContent, DialogActions, TextField, MenuItem, Stack, Alert,
  Tooltip, Switch, FormControlLabel, CircularProgress, Checkbox, Divider,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import api from '../api';
import { useEsMovil, FILA, ENCABEZADO, botonEncabezado, rotulo } from '../estilos/responsivo';
import { TARJETA, TARJETA_INTERACTIVA, TITULO_BLOQUE, ETIQUETA_BLOQUE } from '../estilos/tarjeta';

const TIPOS_CAMPO = [
  ['texto', 'Texto corto'], ['texto_largo', 'Texto largo'], ['numero', 'Número'],
  ['fecha', 'Fecha'], ['lista', 'Lista de opciones'], ['si_no', 'Sí / No'],
];
const FASES = [
  ['abierta', 'Recibida'], ['en_proceso', 'En proceso'],
  ['terminada', 'Terminada (lista para facturar)'],
];
const COLOR_FASE = { abierta: '#e0f2fe', en_proceso: '#fef3c7', terminada: '#dcfce7' };

// Lo que llega del servidor, con las opciones de las listas como texto
// editable («Celular, Computador, Otro»).
const aEdicion = (t) => ({
  ...t,
  campos: (t.campos || []).map(c => ({ ...c, opcionesTexto: (c.opciones || []).join(', ') })),
  pasos: (t.pasos || []).map(p => ({ ...p })),
});

const mover = (lista, i, d) => {
  const j = i + d;
  if (j < 0 || j >= lista.length) return lista;
  const l = [...lista]; [l[i], l[j]] = [l[j], l[i]]; return l;
};

export default function TiposOrden() {
  const esMovil = useEsMovil();
  const [tipos, setTipos] = useState([]);
  const [plantillas, setPlantillas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [eligiendo, setEligiendo] = useState(false);
  const [edicion, setEdicion] = useState(null);
  const [errorForm, setErrorForm] = useState('');

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const [t, p] = await Promise.all([
        api.get('/tipos-orden', { params: { incluir_inactivos: true } }),
        api.get('/tipos-orden/plantillas'),
      ]);
      setTipos(t.data || []); setPlantillas(p.data || []); setError('');
    } catch (e) { setError(e.response?.data?.detail || 'No se pudieron cargar los tipos de orden'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { cargar(); }, [cargar]);

  const usarPlantilla = async (clave) => {
    try {
      const { data } = await api.post('/tipos-orden/desde-plantilla', { plantilla: clave });
      setEligiendo(false); cargar();
      // Se abre de una vez para ajustarla: casi siempre hay algo que cambiar.
      setEdicion(aEdicion(data)); setErrorForm('');
    } catch (e) { setError(e.response?.data?.detail || 'No se pudo crear el tipo de orden'); }
  };

  const guardar = async () => {
    const cuerpo = {
      nombre: edicion.nombre, prefijo: edicion.prefijo,
      texto_comprobante: edicion.texto_comprobante || null,
      pide_responsable: Boolean(edicion.pide_responsable),
      permite_productos: Boolean(edicion.permite_productos),
      orden: Number(edicion.orden || 0), activo: Boolean(edicion.activo),
      campos: edicion.campos.map(c => ({
        clave: c.clave || null, nombre: c.nombre, tipo: c.tipo,
        opciones: c.tipo === 'lista'
          ? (c.opcionesTexto || '').split(',').map(x => x.trim()).filter(Boolean) : [],
        obligatorio: Boolean(c.obligatorio), identificador: Boolean(c.identificador),
      })),
      pasos: edicion.pasos.map(p => ({ clave: p.clave || null, nombre: p.nombre, fase: p.fase })),
    };
    try {
      await api.put(`/tipos-orden/${edicion.id}`, cuerpo);
      setEdicion(null); setErrorForm(''); cargar();
    } catch (e) { setErrorForm(e.response?.data?.detail || 'No se pudo guardar'); }
  };

  const borrar = async (t) => {
    if (!window.confirm(`¿Borrar el tipo de orden «${t.nombre}»?`)) return;
    try { await api.delete(`/tipos-orden/${t.id}`); cargar(); }
    catch (e) { setError(e.response?.data?.detail || 'No se pudo borrar'); }
  };

  const setCampo = (i, cambios) => {
    let campos = edicion.campos.map((c, j) => (j === i ? { ...c, ...cambios } : c));
    // Un solo identificador: marcar uno desmarca los demás.
    if (cambios.identificador) campos = campos.map((c, j) => (j === i ? c : { ...c, identificador: false }));
    setEdicion({ ...edicion, campos });
  };
  const setPaso = (i, cambios) => setEdicion({
    ...edicion, pasos: edicion.pasos.map((p, j) => (j === i ? { ...p, ...cambios } : p)) });

  return (
    <Box>
      <Box sx={ENCABEZADO}>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Qué datos pide cada orden y por qué pasos va. Si la empresa solo factura,
            no necesita ninguno y el menú no muestra órdenes.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} {...botonEncabezado(esMovil)}
          onClick={() => setEligiendo(true)}>
          {rotulo(esMovil, 'Agregar', 'Agregar tipo de orden')}
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {loading ? (
        <Box sx={{ p: 6, textAlign: 'center' }}><CircularProgress /></Box>
      ) : tipos.length === 0 ? (
        <Card sx={{ p: 3, textAlign: 'center' }}>
          <Typography sx={{ fontWeight: 700, mb: 0.5 }}>Esta empresa no usa órdenes</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Las órdenes sirven para el trabajo que se hace antes de facturar: un servicio
            en la barbería, un carro en el taller, un equipo en reparación.
          </Typography>
          <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setEligiendo(true)}>
            Agregar tipo de orden
          </Button>
        </Card>
      ) : (
        <Box sx={{ display: 'grid', gap: 1,
                   gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0,1fr))',
                                          xl: 'repeat(3, minmax(0,1fr))' } }}>
          {/* Llevan chips y botones de color: el fondo común de la skill. */}
          {tipos.map(t => (
            <Card key={t.id} sx={{ ...TARJETA_INTERACTIVA, opacity: t.activo ? 1 : 0.6 }}>
              <Stack direction="row" alignItems="flex-start" spacing={1}>
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Typography sx={TITULO_BLOQUE}>
                    {t.nombre}
                    <Typography component="span" sx={{ ml: 1, fontSize: 12, color: 'text.secondary',
                      fontFamily: 'ui-monospace, monospace' }}>{t.prefijo}-…</Typography>
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                    {t.ordenes} {t.ordenes === 1 ? 'orden' : 'órdenes'}
                    {!t.activo && ' · desactivado'}
                  </Typography>
                </Box>
                <Tooltip title="Editar">
                  <IconButton size="small" onClick={() => { setEdicion(aEdicion(t)); setErrorForm(''); }}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Borrar">
                  <IconButton size="small" color="error" onClick={() => borrar(t)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
              <Typography sx={{ ...ETIQUETA_BLOQUE, color: '#475569', mt: 0.75 }}>Campos</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                {t.campos.length === 0 && (
                  <Typography sx={{ fontSize: 12.5, color: 'text.disabled' }}>
                    Ninguno: cliente y servicios
                  </Typography>
                )}
                {t.campos.map(c => (
                  <Chip key={c.clave} size="small" label={c.nombre}
                    variant={c.identificador ? 'filled' : 'outlined'}
                    color={c.identificador ? 'primary' : 'default'}
                    sx={{ height: 22, fontSize: 11.5,
                          ...(c.identificador ? {} : { bgcolor: '#fff', borderColor: 'transparent' }) }} />
                ))}
              </Box>
              <Typography sx={{ ...ETIQUETA_BLOQUE, color: '#475569', mt: 0.75 }}>Pasos</Typography>
              <Typography sx={{ fontSize: 12.5, mt: 0.25 }}>
                {t.pasos.map(p => p.nombre).join(' → ')}
              </Typography>
            </Card>
          ))}
        </Box>
      )}

      {/* Escoger plantilla */}
      <Dialog fullScreen={esMovil} open={eligiendo} onClose={() => setEligiendo(false)}
              fullWidth maxWidth="md">
        <DialogTitle>¿Qué clase de orden?</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Escoge la plantilla más parecida a tu negocio. Se copia y la ajustas a tu gusto.
          </Typography>
          <Box sx={{ display: 'grid', gap: 1.25,
                     gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0,1fr))' } }}>
            {plantillas.map(p => (
              <Card key={p.clave} sx={{ bgcolor: TARJETA.fondoInteractivo }}>
                <CardActionArea sx={{ p: TARJETA.relleno, height: '100%', alignItems: 'flex-start',
                                      display: 'flex', flexDirection: 'column' }}
                  onClick={() => usarPlantilla(p.clave)}>
                  <Typography sx={TITULO_BLOQUE}>{p.rubro}</Typography>
                  <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mb: 0.75 }}>
                    {p.descripcion}
                  </Typography>
                  <Typography sx={{ fontSize: 11.5 }}>
                    <b>{p.nombre}</b>
                    {p.campos.length ? ` · ${p.campos.join(', ')}` : ''}
                  </Typography>
                  <Typography sx={{ fontSize: 11.5, color: 'text.secondary' }}>
                    {p.pasos.join(' → ')}
                  </Typography>
                </CardActionArea>
              </Card>
            ))}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <Button onClick={() => setEligiendo(false)}>Cancelar</Button>
        </DialogActions>
      </Dialog>

      {/* Editor */}
      <Dialog fullScreen={esMovil} open={Boolean(edicion)} onClose={() => setEdicion(null)}
              fullWidth maxWidth="md">
        <DialogTitle>Tipo de orden</DialogTitle>
        <DialogContent dividers>
          {errorForm && <Alert severity="error" sx={{ mb: 2 }}>{errorForm}</Alert>}
          {edicion && (
            <Stack spacing={2} sx={{ pt: 1 }}>
              <Stack direction={FILA} spacing={2}>
                <TextField size="small" label="Nombre *" fullWidth value={edicion.nombre}
                  helperText="Como se llama en tu negocio: «Orden de trabajo», «Servicio»…"
                  onChange={e => setEdicion({ ...edicion, nombre: e.target.value })} />
                <TextField size="small" label="Prefijo *" sx={{ width: { sm: 200 }, flexShrink: 0 }}
                  value={edicion.prefijo}
                  helperText={`Ej.: ${(edicion.prefijo || 'XX').toUpperCase()}-PRI-0001`}
                  onChange={e => setEdicion({ ...edicion, prefijo: e.target.value.toUpperCase() })} />
              </Stack>

              <Divider textAlign="left" sx={{ fontSize: 12, color: 'text.secondary' }}>
                Datos que pide la orden
              </Divider>
              <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mt: -1 }}>
                El cliente, quién atiende y los servicios ya vienen. Aquí van los datos propios
                de tu negocio. El <b>identificador</b> sirve para encontrar el historial: la
                placa, el serial…
              </Typography>
              {edicion.campos.map((c, i) => (
                <Box key={i} sx={{ p: 1.25, border: '1px solid #e2e8f0', borderRadius: 1.5 }}>
                  <Stack direction={FILA} spacing={1} alignItems={{ sm: 'center' }}>
                    <TextField size="small" label="Nombre del dato" fullWidth value={c.nombre}
                      onChange={e => setCampo(i, { nombre: e.target.value })} />
                    <TextField select size="small" label="Tipo" sx={{ minWidth: 170 }} value={c.tipo}
                      onChange={e => setCampo(i, { tipo: e.target.value,
                        identificador: ['texto', 'numero'].includes(e.target.value) && c.identificador })}>
                      {TIPOS_CAMPO.map(([v, l]) => <MenuItem key={v} value={v}>{l}</MenuItem>)}
                    </TextField>
                    <Stack direction="row" alignItems="center" sx={{ flexShrink: 0 }}>
                      <IconButton size="small" onClick={() =>
                        setEdicion({ ...edicion, campos: mover(edicion.campos, i, -1) })}>
                        <ArrowUpwardIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() =>
                        setEdicion({ ...edicion, campos: mover(edicion.campos, i, 1) })}>
                        <ArrowDownwardIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() =>
                        setEdicion({ ...edicion, campos: edicion.campos.filter((_, j) => j !== i) })}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </Stack>
                  {c.tipo === 'lista' && (
                    <TextField size="small" label="Opciones, separadas por coma" fullWidth
                      sx={{ mt: 1 }} value={c.opcionesTexto || ''}
                      placeholder="Celular, Computador, Tableta"
                      onChange={e => setCampo(i, { opcionesTexto: e.target.value })} />
                  )}
                  <Stack direction="row" spacing={2} sx={{ mt: 0.5 }}>
                    <FormControlLabel control={<Checkbox size="small" checked={Boolean(c.obligatorio)}
                      onChange={e => setCampo(i, { obligatorio: e.target.checked })} />}
                      label={<Typography sx={{ fontSize: 13 }}>Obligatorio</Typography>} />
                    {['texto', 'numero'].includes(c.tipo) && (
                      <FormControlLabel control={<Checkbox size="small" checked={Boolean(c.identificador)}
                        onChange={e => setCampo(i, { identificador: e.target.checked })} />}
                        label={<Typography sx={{ fontSize: 13 }}>Es el identificador</Typography>} />
                    )}
                  </Stack>
                </Box>
              ))}
              <Box>
                <Button size="small" startIcon={<AddIcon />} onClick={() => setEdicion({
                  ...edicion, campos: [...edicion.campos,
                    { nombre: '', tipo: 'texto', opcionesTexto: '', obligatorio: false, identificador: false }] })}>
                  Agregar dato
                </Button>
              </Box>

              <Divider textAlign="left" sx={{ fontSize: 12, color: 'text.secondary' }}>
                Pasos
              </Divider>
              <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mt: -1 }}>
                Por dónde va la orden. La fase le dice al sistema qué se puede hacer: lo
                terminado ya no se edita y es lo único que se factura.
              </Typography>
              {edicion.pasos.map((p, i) => (
                <Stack key={i} direction={FILA} spacing={1} alignItems={{ sm: 'center' }}
                  sx={{ pl: 1, borderLeft: `4px solid ${COLOR_FASE[p.fase] || '#e2e8f0'}` }}>
                  <TextField size="small" label={`Paso ${i + 1}`} fullWidth value={p.nombre}
                    onChange={e => setPaso(i, { nombre: e.target.value })} />
                  <TextField select size="small" label="Fase" sx={{ minWidth: 250 }} value={p.fase}
                    onChange={e => setPaso(i, { fase: e.target.value })}>
                    {FASES.map(([v, l]) => <MenuItem key={v} value={v}>{l}</MenuItem>)}
                  </TextField>
                  <Stack direction="row" sx={{ flexShrink: 0 }}>
                    <IconButton size="small" onClick={() =>
                      setEdicion({ ...edicion, pasos: mover(edicion.pasos, i, -1) })}>
                      <ArrowUpwardIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() =>
                      setEdicion({ ...edicion, pasos: mover(edicion.pasos, i, 1) })}>
                      <ArrowDownwardIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" color="error" onClick={() =>
                      setEdicion({ ...edicion, pasos: edicion.pasos.filter((_, j) => j !== i) })}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                </Stack>
              ))}
              <Box>
                <Button size="small" startIcon={<AddIcon />} onClick={() => setEdicion({
                  ...edicion, pasos: [...edicion.pasos, { nombre: '', fase: 'en_proceso' }] })}>
                  Agregar paso
                </Button>
              </Box>

              <Divider textAlign="left" sx={{ fontSize: 12, color: 'text.secondary' }}>
                Comprobante y opciones
              </Divider>
              <TextField size="small" label="Texto al pie del comprobante" fullWidth multiline rows={2}
                value={edicion.texto_comprobante || ''}
                placeholder="Condiciones, avisos: «No respondemos por objetos dejados…»"
                onChange={e => setEdicion({ ...edicion, texto_comprobante: e.target.value })} />
              <Stack direction={FILA} spacing={1} flexWrap="wrap">
                <FormControlLabel control={<Switch checked={Boolean(edicion.pide_responsable)}
                  onChange={e => setEdicion({ ...edicion, pide_responsable: e.target.checked })} />}
                  label="Pedir quién atiende" />
                <FormControlLabel control={<Switch checked={Boolean(edicion.permite_productos)}
                  onChange={e => setEdicion({ ...edicion, permite_productos: e.target.checked })} />}
                  label="Permitir productos además de servicios" />
                <FormControlLabel control={<Switch checked={Boolean(edicion.activo)}
                  onChange={e => setEdicion({ ...edicion, activo: e.target.checked })} />}
                  label="Activo" />
              </Stack>
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setEdicion(null)}>Cancelar</Button>
          <Button variant="contained" onClick={guardar}>Guardar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
